import { useCallback, useEffect, useMemo, useState } from 'react';

import { ArrowUpIcon } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { Button } from '@/components/ui/button';
import useBrains from '@/hooks/useBrains';
import useIntents from '@/hooks/useIntents';
import { useTemplates } from '@/hooks/useTemplates';
import { DEFAULT_START_MESSAGE } from '@/util/constants';

type Props = { brainId: string; onOptionClick: (message: string) => void };

export const AutoStart = ({ brainId, onOptionClick }: Props) => {
  const { t } = useTranslation();

  const { brain } = useBrains(brainId);
  const { intents } = useIntents(brainId);
  const { templateBrain } = useTemplates();

  const [expression, setExpression] = useState<string | null>(null);

  const getGreetingExpression = useCallback(async () => {
    if (!brain?.language || intents === undefined) {
      return;
    }

    const greetingsIntent = intents.find((i) => i.intent === 'greetings');
    const localExpression =
      greetingsIntent?.expressions?.find((e) => e !== DEFAULT_START_MESSAGE) ?? null;

    let resolvedExpression = localExpression;

    if (localExpression === null) {
      const templateBrainData = await templateBrain(brain.language);
      resolvedExpression =
        templateBrainData?.intents
          ?.find((i) => i.intent === 'greetings')
          ?.expressions?.find((e) => e !== DEFAULT_START_MESSAGE) ?? null;
    }

    setExpression(resolvedExpression ?? t('try_it.empty_state.hello'));
  }, [brain?.language, intents, templateBrain, t]);

  useEffect(() => {
    getGreetingExpression();
  }, [getGreetingExpression]);

  const options = useMemo(
    () => [
      { label: t('try_it.empty_state.default_start'), message: DEFAULT_START_MESSAGE },
      ...(expression != null ? [{ label: expression, message: expression }] : []),
    ],
    [expression, t]
  );

  return (
    <div className="mb-auto flex max-w-xs flex-col items-center gap-6 self-center pt-6">
      <div className="flex flex-col gap-3 text-center">
        <h2 className="text-2xl leading-none font-semibold tracking-tight text-foreground">
          {t('try_it.empty_state.title')}
        </h2>
        <p className="text-sm leading-5 text-muted-foreground">
          {t('try_it.empty_state.description')}
        </p>
      </div>

      <div className="flex w-full flex-col gap-3">
        {options.map(({ label, message }) => (
          <Button
            key={message}
            variant="outline-neutral"
            className="h-auto justify-between gap-3 px-4 py-3 text-left whitespace-normal"
            onClick={() => onOptionClick(message)}
          >
            <span className="min-w-0 text-sm font-medium break-words">{label}</span>
            <ArrowUpIcon size={16} />
          </Button>
        ))}
      </div>
    </div>
  );
};
